import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useWorkspaceStore from '../../../../store/workspace/workspaceStore';
import useChatActions from '../../../../components/agno/hooks/useChatActions';
import { SignalChatBuilder, type SignalBuilderDraftConfig } from './components/SignalChatBuilder';
import { readAutomation } from '../../../../services/automation/automationEndpoints';

/**
 * Convert a backend automation record into a SignalBuilderDraftConfig
 * so the AI builder can pre-load it for editing.
 */
const automationToDraft = (auto: any): SignalBuilderDraftConfig => ({
  name: auto.name || '',
  description: auto.description || '',
  trigger: auto.trigger || 'usage_based',
  entity_relation: auto.entity_relation || 'accounts',
  filters: auto.filters || { predicates: [], query_type: 'AND' },
  metrics: auto.metrics || [],
  sort: auto.sort || undefined,
  selected_fields: auto.selected_fields || [],
  automation_actions: auto.automation_actions || { actions: [], suppression_period: 'all_time' },
});

const SignalAIBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const { initialize } = useChatActions();

  const editSignalId = searchParams.get('signalId');
  const [editDraft, setEditDraft] = useState<SignalBuilderDraftConfig | null>(null);
  const [editSignalName, setEditSignalName] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!editSignalId);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Load existing signal config when editing
  useEffect(() => {
    if (!editSignalId || !workspace?.id) return;

    const loadSignal = async () => {
      try {
        const res = await readAutomation({
          workspace_id: workspace.id,
          automation_id: editSignalId,
        });
        const auto = (res as any)?.data;
        if (auto) {
          const draft = automationToDraft(auto);
          setEditDraft({ ...draft, editing_signal_id: editSignalId });
          setEditSignalName(auto.name || 'Untitled Signal');
        }
      } catch (e) {
        console.error('Failed to load signal for editing:', e);
      } finally {
        setLoading(false);
      }
    };
    loadSignal();
  }, [editSignalId, workspace?.id]);

  const handleBack = () => {
    navigate(`/home/${workspace?.unique_identifier}/signals`);
  };

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-200px)] items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading signal...</div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] min-h-[500px]">
      <SignalChatBuilder
        onBack={handleBack}
        title={editSignalId ? 'Edit Signal with ORA' : 'Create Signal with ORA'}
        subtitle={editSignalId ? `Editing: ${editSignalName}` : 'Follow the steps to build your signal'}
        draftSignalConfig={editDraft}
        onDraftSignalConfigChange={setEditDraft}
        activeSignalName={editSignalName}
        externalPrompt={editSignalId ? `I'm editing the signal "${editSignalName}". Show me a summary of its current configuration and ask what I'd like to change.` : null}
      />
    </div>
  );
};

export default SignalAIBuilderPage;
